import { useState, useEffect } from 'react';
import { Save, ShieldCheck, KeyRound, Loader2 } from 'lucide-react';
import { systemService, tenantSettingsService } from '../../../services';
import type {
  SecuritySettings,
  DiscoverySettings,
  AuthSecurityResponse,
  KcBruteForceSettings,
} from '../../../services';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface SecuritySettingsTabProps {
  settings: SecuritySettings | null;
  onUpdate: (settings: SecuritySettings) => void;
  discoverySettings: DiscoverySettings | null;
  onDiscoveryUpdate: (settings: DiscoverySettings) => void;
  isSuperAdmin?: boolean;
  tenantId?: string;
}

const SecuritySettingsTab = ({ settings, onUpdate, discoverySettings, onDiscoveryUpdate, isSuperAdmin = true, tenantId = '' }: SecuritySettingsTabProps) => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<SecuritySettings | null>(settings);
  const [discoveryData, setDiscoveryData] = useState<DiscoverySettings | null>(discoverySettings);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  useEffect(() => {
    setDiscoveryData(discoverySettings);
  }, [discoverySettings]);

  const handleSave = async () => {
    if (!formData) return;

    try {
      setSaving(true);

      if (isSuperAdmin) {
        const promises: Promise<any>[] = [
          systemService.updateSecurity(formData),
        ];
        if (discoveryData) {
          promises.push(systemService.updateDiscovery(discoveryData));
        }

        const results = await Promise.all(promises);
        onUpdate(results[0]);
        if (results[1]) {
          onDiscoveryUpdate(results[1]);
        }
      } else {
        // TenantAdmin: save overrides to tenant_settings
        const securityOverrides: Record<string, any> = {
          sessionManagement: formData.sessionManagement,
        };
        if (discoveryData) {
          securityOverrides.autoApproveEdges = discoveryData.autoApproveEdges;
        }
        await tenantSettingsService.updateCategory(tenantId, 'security', securityOverrides);
        onUpdate(formData);
        if (discoveryData) onDiscoveryUpdate(discoveryData);
      }

      toast.success('Settings Updated', 'Security settings updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update security settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!formData) {
    return <div className="text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {isSuperAdmin && <LoginSecurityCard />}

      {/* Session Management */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Management</h2>
        <div className="space-y-4">
          <FormField
            label="Session Timeout (minutes)"
            value={formData.sessionManagement.timeoutMinutes.toString()}
            onChange={(e) => {
              // VSNP-721: floor at 5 min — a misconfiguration of 0 / 1
              // would log the operator out before they could fix it.
              // Mirrors the backend clamp in sessionService.ts.
              const raw = parseInt(e.target.value) || 480;
              const clamped = Math.max(5, raw);
              setFormData({
                ...formData,
                sessionManagement: { ...formData.sessionManagement, timeoutMinutes: clamped }
              });
            }}
            type="number"
            min={5}
            helpText="Hard cap on session lifetime — users must re-authenticate every N minutes regardless of activity. Default 480 (8 hours). Set lower for higher-security environments. Minimum 5."
          />
          <FormField
            label="Idle Timeout (minutes)"
            value={formData.sessionManagement.idleTimeoutMinutes.toString()}
            onChange={(e) => {
              const raw = parseInt(e.target.value) || 15;
              const clamped = Math.max(5, raw);
              setFormData({
                ...formData,
                sessionManagement: { ...formData.sessionManagement, idleTimeoutMinutes: clamped }
              });
            }}
            type="number"
            min={5}
            helpText="Logs the user out after N minutes of no keyboard / mouse activity. Default 15. Minimum 5."
          />
          <FormField
            label="Remember Me Duration (days)"
            value={formData.sessionManagement.rememberMeDays.toString()}
            onChange={(e) => setFormData({
              ...formData,
              sessionManagement: { ...formData.sessionManagement, rememberMeDays: parseInt(e.target.value) || 30 }
            })}
            type="number"
          />
          <FormField
            label="Max Concurrent Sessions"
            value={formData.sessionManagement.maxConcurrentSessions.toString()}
            onChange={(e) => setFormData({
              ...formData,
              sessionManagement: { ...formData.sessionManagement, maxConcurrentSessions: parseInt(e.target.value) || 5 }
            })}
            type="number"
          />
          <ToggleField
            label="Force Logout on Password Change"
            checked={formData.sessionManagement.forceLogoutOnPasswordChange}
            onChange={(checked) => setFormData({
              ...formData,
              sessionManagement: { ...formData.sessionManagement, forceLogoutOnPasswordChange: checked }
            })}
          />
        </div>
      </div>

      {/* Edge Provisioning */}
      {discoveryData && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Edge Provisioning</h2>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-700">Auto-Approve New Edges</label>
              <p className="text-xs text-gray-500 mt-0.5">Automatically approve edges registered with valid deployment tokens</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={discoveryData.autoApproveEdges || false}
                onChange={(e) => setDiscoveryData({ ...discoveryData, autoApproveEdges: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      )}

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Login Security — surfaces the real Keycloak realm settings.
 *
 * Password policy is read-only here (change via KC admin console). Brute-force
 * detection is editable so admins can tune the failure threshold and lockout
 * duration without touching KC directly.
 */
const LoginSecurityCard = () => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [data, setData] = useState<AuthSecurityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<KcBruteForceSettings | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const result = await systemService.getAuthSecurity();
        setData(result);
        setDraft(result.bruteForce);
      } catch (err: any) {
        toast.error('Failed to load', err?.message || 'Could not load login security settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const result = await systemService.updateAuthSecurity(draft);
      setData(result);
      setDraft(result.bruteForce);
      toast.success('Saved', 'Login security settings updated');
    } catch (err: any) {
      toast.error('Save failed', err?.response?.data?.error?.message || err?.message || 'Could not update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading login security…</span>
      </div>
    );
  }

  if (!data || !draft) return null;

  const policy = data.passwordPolicy;
  const bf = draft;

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <ShieldCheck className="w-5 h-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-900">Login Security</h2>
      </div>

      {/* Password policy — read-only */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-2">
          <KeyRound className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Password Policy</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1 ml-6 list-disc">
          {policy.minLength ? <li>At least {policy.minLength} characters</li> : null}
          {policy.maxLength ? <li>At most {policy.maxLength} characters</li> : null}
          {policy.upperCase ? <li>At least {policy.upperCase} uppercase letter{policy.upperCase > 1 ? 's' : ''}</li> : null}
          {policy.lowerCase ? <li>At least {policy.lowerCase} lowercase letter{policy.lowerCase > 1 ? 's' : ''}</li> : null}
          {policy.digits ? <li>At least {policy.digits} digit{policy.digits > 1 ? 's' : ''}</li> : null}
          {policy.specialChars ? <li>At least {policy.specialChars} special character{policy.specialChars > 1 ? 's' : ''}</li> : null}
          {policy.notUsername ? <li>Cannot equal username</li> : null}
          {policy.notEmail ? <li>Cannot contain email address</li> : null}
          {policy.passwordHistory ? <li>Cannot reuse last {policy.passwordHistory} passwords</li> : null}
          {!policy.raw && <li className="text-gray-500 italic">No policy configured (minimum length enforced)</li>}
        </ul>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Policy is read-only here. Change via the Keycloak admin console if required.
        </p>
      </div>

      {/* Brute-force detection */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Account Lockout (Brute-Force Protection)</h3>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Enable brute-force detection</label>
            <p className="text-xs text-gray-500 mt-0.5">
              Automatically lock accounts after repeated failed login attempts.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={bf.bruteForceProtected}
              onChange={(e) => setDraft({ ...bf, bruteForceProtected: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        {bf.bruteForceProtected && (
          <div className="space-y-4">
            <FormField
              label="Failures before lockout"
              value={bf.failureFactor.toString()}
              onChange={(e) => setDraft({ ...bf, failureFactor: parseInt(e.target.value) || 30 })}
              type="number"
            />
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">Permanent lockout</label>
                <p className="text-xs text-gray-500 mt-0.5">
                  If enabled, locked accounts stay locked until an admin clears them.
                  Otherwise the lockout expires automatically.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={bf.permanentLockout}
                  onChange={(e) => setDraft({ ...bf, permanentLockout: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
            {!bf.permanentLockout && (
              <FormField
                label="Max lockout duration (seconds)"
                value={bf.maxFailureWaitSeconds.toString()}
                onChange={(e) => setDraft({ ...bf, maxFailureWaitSeconds: parseInt(e.target.value) || 900 })}
                type="number"
              />
            )}
            <FormField
              label="Wait increment between failures (seconds)"
              value={bf.waitIncrementSeconds.toString()}
              onChange={(e) => setDraft({ ...bf, waitIncrementSeconds: parseInt(e.target.value) || 60 })}
              type="number"
            />
          </div>
        )}

        <div className="flex justify-end mt-4">
          {canUpdate && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{saving ? 'Saving…' : 'Save login security'}</span>
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  type = 'text',
  helpText,
  min,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'number';
  helpText?: string;
  min?: number;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      min={min}
      className="input-base w-full"
    />
    {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
  </div>
);

const ToggleField = ({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex items-center justify-between">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
    </label>
  </div>
);

export default SecuritySettingsTab;
